import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import SiteHeader from "../components/SiteHeader";
import SiteFooter from "../components/SiteFooter";
import { fetchSinglePost, fetchPostList, blogConfigured, formatDate } from "../blogData";
import { useSEO } from "../components/useSEO";
import "./BlogPost.css";

/**
 * Real post reader at /blog/:slug — a real route param instead of the
 * original's ?post= query string, so links are clean and shareable.
 * Renders the post's real bodyHTML via dangerouslySetInnerHTML: this
 * is trusted, site-owner-authored content coming from the connected
 * GitHub repo (or the local fallback demo post), not arbitrary
 * user input, so this is the correct, standard way to render it —
 * same as any real blog/CMS renders its own authored HTML content.
 */
export default function BlogPost() {
  const { slug } = useParams();
  const [status, setStatus] = useState("loading"); // loading | found | not-found
  const [post, setPost] = useState(null);
  const [sidebarPosts, setSidebarPosts] = useState([]);
  const [configured] = useState(blogConfigured());

  useSEO(
    post ? `${post.title} | Modus` : status === "not-found" ? "Post not found | Modus" : "Loading… | Modus",
    post ? post.excerpt || undefined : undefined
  );

  useEffect(() => {
    let cancelled = false;
    setStatus("loading");
    Promise.all([fetchSinglePost(slug), fetchPostList()]).then(([foundPost, allPosts]) => {
      if (cancelled) return;
      if (!foundPost) {
        setStatus("not-found");
        return;
      }
      setPost(foundPost);
      setSidebarPosts(allPosts.filter((p) => p.slug !== slug).slice(0, 5));
      setStatus("found");
    });
    return () => {
      cancelled = true;
    };
  }, [slug]);

  return (
    <>
      <SiteHeader active="resources" />

      <section className="post-hero">
        <div className="wrap">
          <Link className="back-link" to="/resources">
            ← Back to Resources
          </Link>
          <h1 id="post-title">{status === "loading" ? "Loading…" : post ? post.title : "Post not found"}</h1>
          {post && (
            <div className="post-meta">
              <span>{formatDate(post.date)}</span>
              <span>·</span>
              <span>{post.author}</span>
            </div>
          )}
          {!configured && status === "found" && (
            <span className="preview-notice" style={{ display: "inline-flex" }}>
              ● Preview mode: connect a GitHub repo to go live
            </span>
          )}
        </div>
      </section>

      <div className="wrap">
        <div className="post-layout">
          <article>
            {status === "loading" && <p>Loading post…</p>}
            {status === "not-found" && (
              <p>
                We couldn't find that post.{" "}
                <Link to="/resources">Back to Resources</Link>
              </p>
            )}
            {status === "found" && post && (
              <div className="post-content-in" dangerouslySetInnerHTML={{ __html: post.bodyHTML }} />
            )}
          </article>

          {sidebarPosts.length > 0 && (
            <aside className="post-content-in" style={{ animationDelay: "0.1s" }}>
              <div className="sidebar-card">
                <h3>Recent Posts</h3>
                {sidebarPosts.map((p) => (
                  <Link key={p.slug} className="sidebar-item" to={`/blog/${encodeURIComponent(p.slug)}`}>
                    <div className="sidebar-item-title">{p.title}</div>
                    <div className="sidebar-item-date">{formatDate(p.date)}</div>
                  </Link>
                ))}
              </div>
            </aside>
          )}
        </div>
      </div>

      <SiteFooter />
    </>
  );
}